/**
 * /earthworks/quoted-vs-actual — every job type, how many completed jobs it has
 * with a quote, and whether it has reached the 5 jobs that suggestions need
 * (EW-8, GET /earthworks/quoted-vs-actual). Full width; a job type opens its
 * own summary page.
 */
import { useNavigate } from 'react-router-dom';
import { Scale } from 'lucide-react';
import { PageHeaderBand } from '../../../components/layout/PageHeaderBand';
import { useAuth } from '../../../contexts/AuthContext';
import { useEwQvaIndex } from '../../../hooks/useEwQva';
import { canReadEwRates } from '../../../lib/earthworks';
import type { EwQvaTypeRow } from '../../../types/ewQva';
import { useOpenRecord } from '../../downhole/recordPage';
import { EW_EYEBROW, EwEmpty, EwPanel, EwRegister, type EwColumn } from '../EwBits';

type Row = EwQvaTypeRow & { id: string };

function stateText(r: EwQvaTypeRow): string {
  if (r.quotedCompletedJobs === 0) return 'No completed jobs with a quote';
  if (!r.hasAiQuote) return 'Variances only — no AI quote prices this type';
  if (!r.thresholdMet) return `Variances only — ${r.quotedCompletedJobs} of ${r.minimumJobs} jobs`;
  return r.draftSuggestions ? `${r.draftSuggestions} suggestion${r.draftSuggestions === 1 ? '' : 's'} to review` : 'Suggestions available';
}

export default function EwQvaScreen() {
  const navigate = useNavigate();
  const openRecord = useOpenRecord();
  const { user } = useAuth();
  const canRead = canReadEwRates(user?.role);
  const query = useEwQvaIndex(canRead);
  // Types with completed work first; the rest after, so the list is the full catalogue.
  const rows: Row[] = [...(query.data ?? [])]
    .sort((a, b) => b.completedJobs - a.completedJobs || a.label.localeCompare(b.label))
    .map((r) => ({ ...r, id: r.jobType }));
  const toReview = rows.reduce((s, r) => s + r.draftSuggestions, 0);

  const columns: EwColumn<Row>[] = [
    { key: 'type', header: 'Job type', render: (r) => <span className="font-bold text-white">{r.label}</span> },
    { key: 'completed', header: 'Completed', align: 'right', render: (r) => String(r.completedJobs) },
    { key: 'quoted', header: 'With a quote', align: 'right', render: (r) => String(r.quotedCompletedJobs) },
    { key: 'state', header: 'Suggestions', render: (r) => <span className={r.draftSuggestions ? 'font-bold text-fi-amber' : 'text-fieldiq-muted'}>{stateText(r)}</span> },
  ];

  return (
    <div data-testid="ew-qva-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow={EW_EYEBROW}
        title="Quoted vs Actual"
        description="How completed jobs ran against what was quoted, by job type. Suggestions for the AI-quote defaults start at 5 completed jobs of a type."
        onBack={() => navigate(-1)}
        backLabel="Back"
        stats={[
          { icon: <Scale className="size-4" aria-hidden="true" />, label: 'Completed jobs', tone: 'neutral', loading: query.isLoading, value: query.isLoading ? undefined : String(rows.reduce((s, r) => s + r.completedJobs, 0)) },
          { icon: <Scale className="size-4" aria-hidden="true" />, label: 'Suggestions to review', tone: 'accent', loading: query.isLoading, value: query.isLoading ? undefined : String(toReview) },
        ]}
      />
      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          <EwPanel eyebrow="Quoted vs actual" title="Job types" testId="ew-qva-types">
            {!canRead ? (
              <EwEmpty>Quoted vs actual compares quotes and prices, so it is for admins, office staff, supervisors and dispatchers.</EwEmpty>
            ) : (
              <EwRegister
                query={query}
                rows={rows}
                columns={columns}
                rail={(r) => (r.draftSuggestions ? 'border-l-fi-amber' : r.thresholdMet ? 'border-l-fi-green' : 'border-l-transparent')}
                onOpen={(r) => openRecord(`/earthworks/quoted-vs-actual/${encodeURIComponent(r.jobType)}`)}
                subject="job types"
                testId="ew-qva-type"
                minWidth="min-w-[760px]"
                empty={<EwEmpty>No job types yet.</EwEmpty>}
                card={(r) => (
                  <>
                    <p className="text-sm font-bold text-white">{r.label}</p>
                    <p className="mt-0.5 text-xs text-fieldiq-muted">{r.completedJobs} completed · {r.quotedCompletedJobs} with a quote</p>
                    <p className="mt-1 text-xs">{stateText(r)}</p>
                  </>
                )}
              />
            )}
          </EwPanel>
        </div>
      </section>
    </div>
  );
}
